import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { IUser } from '../../../users/users';
import { differenceBy, isNil } from 'lodash-es';
import { UsersService } from '../../../users/users.service';
import { PermissionService } from '../../../core/permission.service';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

/**
 * Designed for filtering in listviews
 */
@Component({
    selector: 'app-user-autocomplete-chip-list',
    templateUrl: './user-autocomplete-chip-list.component.html',
    styleUrls: ['./user-autocomplete-chip-list.component.scss'],
    standalone: false
})
export class UserAutocompleteChipListComponent implements AfterViewInit {

  @Input() placeholder: Observable<string>;
  @Input() id = 'user-autocomplete-chip-list'
  @ViewChild('userSearch') userSearch: ElementRef<HTMLInputElement>;
  _selectedIds?: string;
  canListUsers = true;

  @Input('selected')
  set selected(userIds: string) {
    this._selectedIds = userIds;
    if (userIds !== undefined) {
      this.populateSubmittedByUsers(userIds);
    } else {
      this.selectedUsers = [];
      this.selectedUsersSub.next([]);
    }
  }

  @Output() userChanged = new EventEmitter<IUser[]>();
  users: IUser[] = [];
  selectedUsers: IUser[] = [];
  selectedUsersSub: BehaviorSubject<IUser[]> = new BehaviorSubject<IUser[]>([]);
  isLoading = false;

  constructor(
    private permissionService: PermissionService,
    private usersService: UsersService,
    public changeDetector: ChangeDetectorRef,
  ) {
  }

  ngAfterViewInit() {
    this.permissionService.hasPermission('USER_VIEW').subscribe(perm => {
      if(perm === true) {
        this.listenToUsersInput();
      } else {
        this.canListUsers = false;
        this.changeDetector.detectChanges();
      }
    });
  }

  listenToUsersInput() {
    fromEvent(this.userSearch.nativeElement, 'keyup')
      .pipe(
        tap(() => this.selectedUsersSub.next([])),
        map((event: any) => event.target.value),
        debounceTime(100),
        distinctUntilChanged(),
        filter((value: string) => value.length >= 2),
        switchMap(value => this.usersService.filterUsersByUserName(value)),
      )
      .subscribe(response => {
        const exceptAlreadySelected: IUser[] = differenceBy(response.list, this.users, 'userId');
        this.selectedUsersSub.next(exceptAlreadySelected);
      });
  }

  onUserSelectedInternal(event: MatAutocompleteSelectedEvent) {
    this.selectedUsers.push(event.option.value);
    this.userSearch.nativeElement.value = '';
    this.selectedUsersSub.next([]);
    this.userChanged.emit(this.selectedUsers);
  }

  remove(user: IUser): void {
    const index = this.selectedUsers.indexOf(user);
    if (index >= 0) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers = [];
    }
    this.selectedUsersSub.next([]);
    this.userChanged.emit(this.selectedUsers);
  }

  private populateSubmittedByUsers(userIdList: string) {
    if (userIdList === null || userIdList === ''){
      return;
    }
    this.usersService.getUsers(userIdList).subscribe(
      result => {
        /* some users might come as null from backend if not found on current env */
        this.selectedUsers = result !== null ? <IUser[]>result.filter(user => !isNil(user)) : [];
        this.userChanged.emit(this.selectedUsers);
      });
  }

}
